import { ISignOnProvider } from 'app/core/models/essette-interfaces';
import { IComponentConfig } from 'app/shared/elements/ess-dynamic-grid';

/**
 * @module app.shared
 */
export interface IEssetteConfig {
    readonly debug: boolean;
    readonly application: string;
    readonly title: string;
    readonly description: string;
    readonly showEnvironment: boolean;
    readonly instanceScope: string;
    readonly instancePrefix: string;
    readonly instanceType: string;
    readonly startupMessage: string;
    readonly optionalFeatures: {
    };
    readonly features: ReadonlyArray<string>;
    readonly modules: ReadonlyArray<string>;
    readonly integrations: ReadonlyArray<string>;
    readonly baseUrl: string;
    readonly agreementFile?: string;
    readonly labels?: {
        readonly pp?: {
            readonly additionalInfo?: string;
        }
    },
    readonly eagleAuthClaimSyncTimeoutSeconds: number;
    readonly iqcHeadlessGuidelineSearchTimeoutSeconds: number;
    readonly passwordRules: {
        readonly minLength?: number;
        readonly maxLength?: number;
        readonly characterTypes?: Array<"letter" | "upperCase" | "lowerCase" | "number" | "symbol">;
        readonly minCharacterTypes?: number;
        readonly minKeywords?: number;
        readonly minKeywordLength?: number;
        readonly custom?: [{
            pattern: string,
            error: string
        }]
    };
    readonly credentials: {
        readonly secondsToAutoLogIn: number;
        readonly loginName: string;
        readonly password: string;
    };
    readonly lockout: {
        readonly minutesToWarning: number;
        readonly minutesToLockout: number;
    };
    readonly session: {
        readonly minutesToWarning: number;
        readonly minutesToDanger: number;
        readonly minutesToExpirationOverride: number;
    };
    readonly logging: {
        readonly logLevel: 'error' | 'warn' | 'info' | 'debug';
        readonly maxQueueSize: number;
        readonly queueAutoSendTimeout: number;
    };
    readonly apiPolling: {
        readonly maxIntervalMilliseconds: number
    };
    readonly showServiceLineNumbers: boolean;
    readonly showMedicationLineNumbers: boolean;
    readonly enableServiceLineNumberFlag: boolean;
    readonly enableAlternateAuthSummary: boolean;
    readonly i18n: {
        readonly language: string;
        readonly currency: string;
        readonly dateLocales: ReadonlyArray<string>;
        readonly debug: boolean;
        readonly skipTranslationOnMissingKey: boolean;
        readonly useBrowserLanguageDetector: boolean;
    };
    readonly views: {
        readonly customConfiguration: string;
    };
    readonly components: Record<string, Partial<IComponentConfig & { includes: string[], excludes: string[] }>>
    readonly help: {
        readonly baseUrl: string;
        readonly defaultTopic: string;
        readonly windowTarget: string;
        readonly topicConfiguration: string;
    };
    readonly printing: {
        readonly scaleMargins: boolean;
        readonly profiles: {
            readonly [profile: string]: {
                readonly size: string;
                readonly landscape: boolean;
                readonly margins: {
                    readonly left: number;
                    readonly right: number;
                    readonly top: number;
                    readonly bottom: number;
                };
            };
        };
    };
    readonly notifications: {
        readonly enabled: boolean;
        readonly updateIntervalSeconds: number;
    };
    readonly tracking: {
        readonly updateIntervalSeconds: number;
    };
    readonly essetteApi: {
        readonly baseUrl: string;
        readonly ignoreVersionCheck: boolean;
    };
    readonly essetteTransitional: {
        readonly enabled: boolean;
        readonly baseUrl: string;
        readonly origins: ReadonlyArray<string>;
    };
    readonly signOnProviders?: ReadonlyArray<ISignOnProvider>;
    readonly testing: {
        readonly enabled: boolean;
    };
    readonly pageConfigurations: {
        readonly cd: {
            readonly member: {
                readonly summary: {
                    readonly showEPMP?: boolean;
                }
            }
        }
        readonly core: {
            readonly assessments: {
                readonly answerHistoryLimit: number;
            }
        },
        readonly cm: {
            readonly case: {
                readonly notes: {
                    readonly barrierChecked: boolean;
                    readonly caseChecked: boolean;
                    readonly goalChecked: boolean;
                    readonly interventionChecked: boolean;
                    readonly outcomeChecked: boolean;
                    readonly taskChecked: boolean;
                },
                readonly ribbon: {
                    readonly modifiedDateUpdateIntervalSeconds: number;
                }
            },
            readonly manageCases: {
                readonly search: {
                    readonly myCasesCaseOwnerChecked: boolean;
                    readonly myCareTeamCasesChecked: boolean;
                    readonly onlyOpenCasesChecked: boolean;
                    readonly onlyClosedCasesChecked: boolean;
                }
            }
        },
        readonly um: {
            readonly auth: {
                readonly modals: {
                    readonly editAuth: {
                        readonly otherReferenceNumberFieldReadonly: boolean;
                    }
                },
                readonly manageAuths: {
                    readonly create: {
                        readonly hideOtherReferenceNumberField: boolean;
                        readonly modals: {
                            readonly copyAuth: {
                                readonly otherReferenceNumberFieldReadonly: boolean;
                            }
                        }
                    }
                }
            }
        }
    };
    readonly externalSystems: {
        cxone?: {
            readonly brandId: string;
            readonly chat: {
                readonly enabled: boolean
            };
        },
        epmp?: {
            readonly sourceUrl: string;
            readonly configUrlContacts: string;
            readonly configUrlPreferences: string;
            readonly idType: string;
            readonly userIdKey: string;
            readonly validGroupNumbers: ReadonlyArray<string>;
        }
    };
    cacheDefaultTtl?: number;
}

export const defaultConfig: IEssetteConfig = {
    debug: false,
    application: 'suite',
    title: 'Essette',
    description: 'EssetteSuite',
    showEnvironment: false,
    instanceScope: 'essette',
    instancePrefix: '',
    instanceType: '',
    startupMessage: '',
    optionalFeatures: {
    },
    features: ['shared', 'core'],
    modules: ['cd'],
    integrations: [],
    baseUrl: '~/',
    passwordRules: {},
    credentials: {
        secondsToAutoLogIn: 0,
        loginName: '',
        password: ''
    },
    lockout: {
        minutesToLockout: 30,
        minutesToWarning: 25
    },
    apiPolling: {
        maxIntervalMilliseconds: 5000
    },
    eagleAuthClaimSyncTimeoutSeconds: 30,
    iqcHeadlessGuidelineSearchTimeoutSeconds: 600,
    showServiceLineNumbers: false,
    showMedicationLineNumbers: false,
    enableServiceLineNumberFlag: false,
    enableAlternateAuthSummary: false,
    session: {
        minutesToWarning: 180,
        minutesToDanger: 30,
        minutesToExpirationOverride: 0
    },
    logging: {
        logLevel: 'info',
        maxQueueSize: 10,
        queueAutoSendTimeout: 10000
    },
    i18n: {
        language: 'en',
        currency: 'USD',
        dateLocales: ['en-US'],
        debug: false,
        skipTranslationOnMissingKey: true,
        useBrowserLanguageDetector: false
    },
    views: {
        customConfiguration: './views-config.json'
    },
    components: {},
    help: {
        baseUrl: '../help/',
        defaultTopic: 'index.html',
        windowTarget: 'help',
        topicConfiguration: ''
    },
    printing: {
        scaleMargins: true,
        profiles: {
            default: {
                size: '',
                landscape: false,
                // points (1in=72pt)
                margins: {
                    left: 54,
                    right: 54,
                    top: 54,
                    bottom: 54
                }
            }
        }
    },
    notifications: {
        enabled: true,
        updateIntervalSeconds: 30
    },
    tracking: {
        updateIntervalSeconds: 10
    },
    essetteApi: {
        baseUrl: '/api/',
        ignoreVersionCheck: false
    },
    essetteTransitional: {
        enabled: false,
        baseUrl: '/transitional/',
        origins: []
    },
    testing: {
        enabled: false
    },
    pageConfigurations: {
        cd: {
            member: {
                summary: {}
            }
        },
        core: {
            assessments: {
                answerHistoryLimit: 500
            }
        },
        cm: {
            case: {
                notes: {
                    barrierChecked: false,
                    caseChecked: true,
                    goalChecked: false,
                    interventionChecked: false,
                    outcomeChecked: false,
                    taskChecked: false
                },
                ribbon: {
                    modifiedDateUpdateIntervalSeconds: 30
                }
            },
            manageCases: {
                search: {
                    myCasesCaseOwnerChecked: true,
                    myCareTeamCasesChecked: false,
                    onlyOpenCasesChecked: true,
                    onlyClosedCasesChecked: false
                }
            }
        },
        um: {
            auth: {
                modals: {
                    editAuth: {
                        otherReferenceNumberFieldReadonly: false
                    }
                },
                manageAuths: {
                    create: {
                        hideOtherReferenceNumberField: false,
                        modals: {
                            copyAuth: {
                                otherReferenceNumberFieldReadonly: false
                            }
                        }
                    }
                }
            }
        }
    },
    externalSystems: {
    },
    cacheDefaultTtl: 1000 * 60 * 3
};

